/**
 * ClienteFormModal - Sprint 2
 *
 * Modal para criar/editar Cliente (Distribuidor)
 * Features: Validação CNPJ, formatação automática, soft delete, ViaCEP integration
 *
 * Orchestrator: logic via useClienteForm, layout via sub-components.
 */
import React from 'react';
import { X, Building2, AlertCircle, Trash2 } from 'lucide-react';
import { Cliente } from '../services/clienteService';
import { useClienteForm } from '../hooks/useClienteForm';
import ClienteFormIdentificacao from './ClienteFormIdentificacao';
import ClienteFormLocalizacao from './ClienteFormLocalizacao';
import ClienteFormSistema from './ClienteFormSistema';

interface ClienteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSave?: () => void;
  onDelete?: (id: number) => void;
  cliente?: Cliente | null;
}

const ClienteFormModal: React.FC<ClienteFormModalProps> = ({
  isOpen,
  onClose,
  cliente,
  onSuccess,
  onSave,
  onDelete,
}) => {
  const {
    formData,
    errors,
    loading,
    error,
    loadingCep,
    estados,
    municipios,
    isEditMode,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleteLoading,
    showReactivateConfirm,
    setShowReactivateConfirm,
    clienteInativoInfo,
    setClienteInativoInfo,
    reactivateLoading,
    handleChange,
    handleSubmit,
    handleDelete,
    handleReactivateCliente,
  } = useClienteForm({ isOpen, cliente, onSuccess, onSave, onDelete, onClose });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isEditMode ? 'Editar Cliente' : 'Novo Cliente'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-3 space-y-2">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-800">{error}</p>
            </div>
          )}

          <input type="hidden" name="codigo" value={formData.codigo} />

          {/* 3-column layout using sub-components */}
          <div className="grid grid-cols-3 gap-3">
            <ClienteFormIdentificacao
              formData={formData}
              errors={errors}
              loading={loading}
              onChange={handleChange}
            />
            <ClienteFormLocalizacao
              formData={formData}
              errors={errors}
              loading={loading}
              loadingCep={loadingCep}
              estados={estados}
              municipios={municipios}
              onChange={handleChange}
            />
            <ClienteFormSistema
              formData={formData}
              errors={errors}
              loading={loading}
              onChange={handleChange}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
            <div>
              {isEditMode && onDelete && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                  disabled={loading || deleteLoading}
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Salvando...' : isEditMode ? 'Atualizar' : 'Criar Cliente'}
              </button>
            </div>
          </div>
        </form>

        {/* Modal de Confirmação de Exclusão */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Excluir Cliente</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Esta ação não pode ser desfeita</p>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Tem certeza que deseja excluir o cliente{' '}
                  <strong className="text-red-600 dark:text-red-400">
                    {cliente?.nome_fantasia || cliente?.razao_social}
                  </strong>?
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Todos os dados associados a este cliente serão removidos permanentemente.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  disabled={deleteLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Excluindo...' : 'Confirmar Exclusão'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmação de Reativação de Cliente Inativo */}
        {showReactivateConfirm && clienteInativoInfo && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cliente Inativo Encontrado</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Este CNPJ pertence a um cliente existente</p>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  O CNPJ informado já está cadastrado no cliente inativo{' '}
                  <strong className="text-amber-600 dark:text-amber-400">{clienteInativoInfo.nome}</strong>.
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Deseja reativar este cliente ao invés de criar um novo?
                </p>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowReactivateConfirm(false);
                    setClienteInativoInfo(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  disabled={reactivateLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleReactivateCliente}
                  className="px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                  disabled={reactivateLoading}
                >
                  {reactivateLoading ? 'Reativando...' : 'Reativar Cliente'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClienteFormModal;
